/**
 * SafeCurves filter config consumed by `mapDataFilterStore` + `DataFilterPanel`.
 *
 * Each filter's `id` is the MapLibre property name on the rendered features
 * (`speed_limit`, `road_system`, `ownership`, `road_seal`); the store's
 * derived expression compares `['get', id]` to each enabled value.
 *
 * Ownership uses `displayOptions` to show a short grouped list, with the
 * `Other` row's `expandsTo` toggling all the long-tail FHWA codes together so
 * the filter expression stays consistent with the visible UI.
 */

import type { FilterConfig, FilterEnumOption } from '#lib/apps/types.js';
import { mapOwnership, mapRoadSeal, mapRoadSystem } from '#lib/common/mappings.js';
import { jurisdictionStore, type Jurisdiction } from '#lib/stores/jurisdictionStore.svelte.js';

// Speed-limit filter values; identical across jurisdictions for now.
const speedLimitsByJurisdiction: Record<Jurisdiction, number[]> = {
	USA: [15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85],
	AUS: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120]
};
const speedLimitLabelByJurisdiction: Record<Jurisdiction, string> = {
	USA: 'Speed limit (MPH)',
	AUS: 'Speed limit (KM/H)'
};

export const getSpeedLimits = (): number[] =>
	speedLimitsByJurisdiction[jurisdictionStore.jurisdiction];

const roadSystemOptions: FilterEnumOption[] = Object.keys(mapRoadSystem).map((k) => ({
	value: k,
	label: mapRoadSystem[Number(k)]
}));

const roadSealOptions: FilterEnumOption[] = Object.keys(mapRoadSeal)
	.sort()
	.reverse() // 1 (Paved) first
	.map((k) => ({ value: k, label: mapRoadSeal[Number(k)] }));

// Ownership: full universe (drives the filter expression) + a short grouped
// display where 'Other' (80) toggles every code that isn't otherwise listed.
const ownershipOptions: FilterEnumOption[] = Object.keys(mapOwnership).map((k) => ({
	value: k,
	label: mapOwnership[Number(k)]
}));

const ownershipDisplayValues = ['1', '2', '3', '4', '50', '80'];
const ownershipOtherExpandsTo = ownershipOptions
	.map((o) => o.value)
	.filter((v) => !ownershipDisplayValues.includes(v));

const ownershipDisplayOptions: FilterEnumOption[] = ownershipDisplayValues.map((v) =>
	v === '80'
		? { value: v, label: mapOwnership[80], expandsTo: ownershipOtherExpandsTo }
		: { value: v, label: mapOwnership[Number(v)] }
);

// Rebuilt on each call so the jurisdiction-dependent speed-limit values/label
// reflect the current `jurisdictionStore.jurisdiction` (set from metadata after
// module load); expose via a getter on the AppConfig to keep it reactive.
export const buildFilters = (): FilterConfig[] => [
	{
		id: 'speed_limit',
		label: speedLimitLabelByJurisdiction[jurisdictionStore.jurisdiction],
		options: getSpeedLimits().map((v) => ({ value: String(v), label: String(v) }))
	},
	{
		id: 'road_system',
		label: 'Road System',
		options: roadSystemOptions
	},
	{
		id: 'ownership',
		label: 'Ownership',
		options: ownershipOptions,
		displayOptions: ownershipDisplayOptions
	},
	{
		id: 'road_seal',
		label: 'Road surface',
		options: roadSealOptions
	}
];
